import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertTriangleIcon, ExternalLinkIcon, XIcon } from 'lucide-react';
import { PENDING, type LegalBlock, type LegalDoc } from '../../data/legal';
import { withdrawalFormUrl } from '../../data/config';
type LegalModalProps = {
  doc: LegalDoc | null;
  onClose: () => void;
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({
      preventScroll: true
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus({
        preventScroll: true
      });
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-177" data-mp="2db0tfrd8udue">
      {doc && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#4A3A35]/50 p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: reduce ? 0 : 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-178" data-mp="28ditgqh7f8yj">
          <motion.div role="dialog" aria-modal="true" aria-labelledby={`legal-title-${doc.id}`} className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-[1.75rem] bg-[#FFFDF9] shadow-[0_30px_60px_-20px_rgba(74,58,53,0.45)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: reduce ? 0 : 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-179" data-mp="2w44q7llrd6t8">
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#EADFCF] bg-white text-[#4A3A35] transition-colors duration-150 hover:bg-[#FBF6EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E74D7A]" data-id="element-180" data-mp="1i2p6p2h4z8gn">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="z78tiziiid6v" />
            </button>

            <div className="overflow-y-auto overscroll-contain px-6 pb-8 pt-8 sm:px-9" data-id="element-182" data-mp="ki2vvfzbov13">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#E74D7A]" data-id="element-183" data-mp="pfw0i4kz9gz9">Recursos Imprimibles AR</p>
              <h2 id={`legal-title-${doc.id}`} className="mt-1 pr-12 font-display text-[1.4rem] font-extrabold leading-tight text-[#4A3A35] sm:text-[1.6rem]" data-id="element-184" data-mp="bho6o5j8mvsb">
                {doc.title}
              </h2>
              <div className="mt-5 space-y-3 text-[15.5px] leading-[1.7] text-body" data-id="element-185" data-mp="3t03izkhwew9q">
                {doc.blocks.map((block, i) => <LegalBlockView key={i} block={block} data-id="element-186" data-mp="5tt3ru2bh8zh" data-mpi={i} />)}
              </div>

              {doc.action === 'withdrawal' && <WithdrawalAction data-id="element-187" data-mp="1qea40lggu3k" />}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function LegalBlockView({
  block
}: {
  block: LegalBlock;
}) {
  if (block.kind === 'heading') {
    return <h3 className="pt-3 font-display text-[16px] font-bold text-[#4A3A35] sm:text-[17px]" data-id="element-188" data-mp="aqt9g3v989q2">{block.text}</h3>;
  }
  if (block.kind === 'list') {
    return <ul className="list-disc space-y-1 pl-5 marker:text-[#E74D7A]" data-id="element-189" data-mp="kjiae4t6i575">
        {block.items.map(item => <li key={item} data-id="element-190" data-mp="yirn2ui6eh6d">{item}</li>)}
      </ul>;
  }
  if (block.kind === 'pending') {
    return <PendingNote text={block.text} data-id="element-191" data-mp="tkq5ke0eepx0" />;
  }
  const isPending = block.text === PENDING;
  return <p data-id="element-192" data-mp="2nyu7w8u4b7pg">
      {block.label && <strong className="font-semibold text-[#4A3A35]" data-id="element-193" data-mp="2nx9ambgf5anw">{block.label} </strong>}
      {isPending ? <span className="rounded-md bg-[#FEEAF1] px-1.5 py-0.5 text-[13px] font-bold uppercase tracking-[0.04em] text-[#C2335F]" data-id="element-194" data-mp="2sw3ulgq4h59l">
          {block.text}
        </span> : block.text}
    </p>;
}
function PendingNote({
  text
}: {
  text: string;
}) {
  return <p className="flex items-start gap-2 rounded-xl border border-dashed border-[#EC6A92] bg-[#FEEAF1] px-3 py-2.5 text-[14px] font-semibold leading-snug text-[#C2335F]" data-id="element-195" data-mp="252c6t0tdpvgj">
      <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-196" data-mp="2e04mw76xd74z" />
      {text}
    </p>;
}
function WithdrawalAction() {
  if (withdrawalFormUrl) {
    return <a href={withdrawalFormUrl} target="_blank" rel="noopener noreferrer" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#E74D7A] px-6 py-3.5 font-display text-[15px] font-extrabold uppercase tracking-tight text-white transition-colors duration-150 ease-out hover:bg-[#DE3F6E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E74D7A] focus-visible:ring-offset-2" data-id="element-197" data-mp="3pgurjlvp9qwh">
        Solicitar la revocación
        <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-198" data-mp="3qs7se2ivyn3j" />
      </a>;
  }
  return <div className="mt-6 space-y-3" data-id="element-199" data-mp="3lsv4dega39zd">
      <PendingNote text={`Mecanismo de revocación: ${PENDING}. Conectar el formulario o mecanismo real antes de publicar.`} data-id="element-200" data-mp="3ltwglm9162io" />
      <button type="button" disabled className="flex w-full cursor-not-allowed items-center justify-center rounded-full bg-[#E8D9CF] px-6 py-3.5 font-display text-[15px] font-extrabold uppercase tracking-tight text-[#8B6B55]" data-id="element-201" data-mp="3gsgz2462ae51">
        Solicitar la revocación (no disponible aún)
      </button>
    </div>;
}